import type { CheckoutModalState } from '@/components/modules/checkout/CheckoutModal/CheckoutModal.hooks';
import {
  CardCheckout,
  getCardPaymentMethod,
  trackPaymentMethodCancelled,
  trackPaymentMethodChange,
  trackPaymentMethodFailed,
  trackPaymentMethodInitiated,
  trackPaymentMethodSelected,
} from '@/helpers/paymentMethodTracking';
import { SelectedPaymentMethod } from '@/types/checkout';
import { PaymentCard } from '@/types/paymentcards';
import { useEffect, useRef } from 'react';

/**
 * Payment method tracking for a checkout whose modal applies gift, wellness and value cards.
 * Opening a card screen sends Payment Method Initiated; the handlers send the rest. An apply
 * result is sent for the card screen of the render that started the apply.
 */
export function useCardPaymentTracking(checkout: CardCheckout, modal: CheckoutModalState) {
  const addedCardIds = useRef(new Set<string>());
  const cardPaymentMethod = getCardPaymentMethod(modal.screen);

  useEffect(() => {
    if (modal.isOpen && cardPaymentMethod) trackPaymentMethodInitiated(cardPaymentMethod, checkout);
  }, [modal.isOpen, modal.screen]);

  return {
    trackCardApplied: ({ success, errorCode }: { success: boolean; errorCode?: string }) => {
      if (!cardPaymentMethod) return;
      if (success) trackPaymentMethodSelected(cardPaymentMethod, checkout, false);
      else trackPaymentMethodFailed(cardPaymentMethod, checkout, errorCode);
    },
    trackCardEntryCancelled: () => {
      if (cardPaymentMethod) trackPaymentMethodCancelled(cardPaymentMethod, checkout);
    },
    trackPaymentMethodChange: (selected: SelectedPaymentMethod, method: SelectedPaymentMethod) =>
      trackPaymentMethodChange(selected, method, checkout, addedCardIds.current),
    trackPaymentCardAdded: (card: PaymentCard) => {
      addedCardIds.current.add(card.id);
    },
  };
}
